import {
  type Affix,
  AFFIXES,
  affixesAllowed,
  describeAffix,
  describeAffixRange,
  type ItemTemplate,
  type ReforgeResult,
  SEAL_RELICS,
} from '@shefira/shared';

import { uiFormat, uiText } from './text.js';

/**
 * The runes of the runesmith's window (ADR 0057): one line per rune with its
 * four choices, the runes a piece may carry, and the last reroll's before
 * and after. Drawing only: the panel keeps the choices and sends the intent.
 */

/**
 * What the reroll does to one rune: kept whole, kept in kind for a new
 * value, drawn anew, or sealed for good.
 */
export type RuneChoice = 'keep' | 'value' | 'new' | 'seal';

const CHOICES: readonly { choice: RuneChoice; label: string; title: string }[] = [
  { choice: 'keep', label: 'Garder', title: 'La rune reste telle quelle (une relique)' },
  { choice: 'value', label: 'Valeur', title: 'Même rune, nouvelle valeur (une relique)' },
  { choice: 'new', label: 'Rune', title: 'Une autre rune, tirée au hasard' },
  {
    choice: 'seal',
    label: 'Sceller',
    title: 'Gardée pour toujours, plus jamais modifiable ({count} reliques, une fois)',
  },
];

export interface RuneRowInput {
  affix: Affix;
  template: ItemTemplate;
  current: RuneChoice;
  /** Whether keeping or sealing one more would leave nothing to draw. */
  wholeFull: boolean;
  onChoose: (choice: RuneChoice) => void;
}

/** A rune, its range on this piece, and its choices; a sealed one has none. */
export function runeRow({ affix, template, current, wholeFull, onChoose }: RuneRowInput): HTMLElement {
  const row = document.createElement('div');
  row.className = 'runesmith__rune';
  const text = document.createElement('span');
  text.className = 'tooltip__affix';
  text.textContent = uiText(describeAffix(affix));
  const range = document.createElement('span');
  range.className = 'tooltip__range';
  range.textContent = uiFormat(' [{range}]', { range: describeAffixRange(affix.id, template) });
  text.append(range);
  if (affix.sealed === true) {
    row.classList.add('runesmith__rune--sealed');
    const seal = document.createElement('span');
    seal.className = 'runesmith__seal';
    seal.textContent = uiText('Scellée pour toujours');
    row.append(text, seal);
    return row;
  }

  const group = document.createElement('div');
  group.className = 'runesmith__choices';
  group.setAttribute('role', 'radiogroup');
  for (const { choice, label, title } of CHOICES) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'runesmith__choice';
    button.textContent = uiText(label);
    button.title = uiFormat(title, { count: SEAL_RELICS });
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', String(choice === current));
    const staysWhole = choice === 'keep' || choice === 'seal';
    const alreadyWhole = current === 'keep' || current === 'seal';
    button.disabled = staysWhole && !alreadyWhole && wholeFull;
    button.addEventListener('click', () => {
      onChoose(choice);
    });
    group.append(button);
  }
  row.append(text, group);
  return row;
}

/** Every rune this piece may carry, with its lowest and highest roll here. */
export function possibleRunes(
  template: ItemTemplate,
  open: boolean,
  onToggle: (open: boolean) => void,
): HTMLElement {
  const details = document.createElement('details');
  details.className = 'runesmith__possible';
  details.open = open;
  details.addEventListener('toggle', () => {
    onToggle(details.open);
  });
  const summary = document.createElement('summary');
  summary.textContent = uiText('Runes possibles sur cette pièce');
  details.append(summary);
  for (const id of affixesAllowed(template)) {
    const entry = document.createElement('p');
    entry.className = 'smith__muted';
    // « +1 à 3 % de chance de critique ».
    entry.textContent = uiText(AFFIXES[id].label.replace('{v}', describeAffixRange(id, template)));
    details.append(entry);
  }
  return details;
}

/** The last reroll: the runes before it and after it, side by side. */
export function beforeAfter(result: ReforgeResult): HTMLElement {
  const table = document.createElement('div');
  table.className = 'runesmith__compare';
  const column = (heading: string, affixes: readonly Affix[]): HTMLElement => {
    const side = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'smith__muted';
    title.textContent = heading;
    side.append(title);
    for (const affix of affixes) {
      const entry = document.createElement('p');
      entry.className = 'tooltip__affix';
      entry.textContent = uiText(describeAffix(affix));
      side.append(entry);
    }
    return side;
  };
  table.append(column(uiText('Avant'), result.before), column(uiText('Après'), result.affixes));
  return table;
}
